import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Household,
  HouseholdRole,
  Interaction,
  Person,
  PersonStatus,
  useHousehold,
  usePatchHousehold,
  usePerson,
  usePersonCallings,
  usePersonInteractions,
  usePatchPerson,
  useDeleteInteraction,
} from '../api';
import ChangeLog from '../components/ChangeLog';
import { PersonGlyph } from '../components/Glyphs';
import LogInteractionForm from '../components/LogInteractionForm';
import InteractionEditForm from '../components/InteractionEditForm';
import { PersonMinisteringPanel } from '../components/Ministering';
import { TagsCard } from '../components/Tags';
import { formatInteractionType } from '../interactionTypes';
import { printPage } from '../print';
import { formatPhone, telHref } from '../phone';

type PersonEdits = Partial<
  Pick<Person, 'name_full' | 'phone' | 'email' | 'birth_date' | 'household_role' | 'notes_override' | 'status_override' | 'occupation'>
>;

const STATUS_OPTIONS: { value: PersonStatus | ''; label: string }[] = [
  { value: '', label: '—' },
  { value: 'active', label: 'Active' },
  { value: 'semi_active', label: 'Semi-Active' },
  { value: 'inactive', label: 'Inactive' },
];

const STATUS_BADGE: Record<PersonStatus, string> = {
  active:      'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300',
  semi_active: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
  inactive:    'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
};

function formatEnum(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const LCR_PROFILE_URL = 'https://lcr.churchofjesuschrist.org/mlt/records/member-profile';

const LINK_BUTTON =
  'inline-flex items-center rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-200';

export default function PersonDetail() {
  const { id = '' } = useParams();
  const person = usePerson(id);
  const callings = usePersonCallings(id);
  const interactions = usePersonInteractions(id);
  const household = useHousehold(person.data?.household_id ?? '');

  if (person.isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (person.error) return <p className="text-red-600 dark:text-red-400">Error loading person.</p>;
  if (!person.data) return null;
  const p = person.data;
  const hh = household.data ?? null;
  const activeMembers = hh?.members?.filter((m) => !!m) ?? [];
  const isSingleOccupant = !!hh && activeMembers.length === 1;

  const lcrProfileId = p.lcr_uuid || p.lcr_id;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_3fr] print:grid-cols-1">
      <section className="space-y-4">
        <header>
          <div className="flex items-start justify-between">
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <PersonGlyph />
              {p.name_full ?? '(unnamed)'}
            </h1>
            <span className="flex items-center gap-2">
              {p.status_override && (
                <span className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[p.status_override]}`}>
                  {formatEnum(p.status_override)}
                </span>
              )}
              <button
                onClick={printPage}
                className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Print or save as PDF"
              >
                Print
              </button>
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {formatEnum(p.household_role)}
            {p.age != null ? ` · ${p.age}y` : ''}
            {p.sex ? ` · ${p.sex}` : ''}
            {p.is_member === false && ' · non-member'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {p.household_id && (
              <Link to={`/households/${p.household_id}`} className={LINK_BUTTON}>
                View household →
              </Link>
            )}
            {p.source === 'lcr' && lcrProfileId ? (
              <a
                href={`${LCR_PROFILE_URL}/${lcrProfileId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_BUTTON}
              >
                View in LCR ↗
              </a>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {p.source === 'lcr' ? 'LCR member' : 'Steward-only'}
              </span>
            )}
          </div>
        </header>

        <PersonInfoCard person={p} />

        <PersonMinisteringPanel personId={p.id} />

        <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="border-b border-slate-200 dark:border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Callings
          </div>
          {callings.data?.callings.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No callings recorded.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
              {callings.data?.callings.map((c) => (
                <li key={c.id} className="flex justify-between px-3 py-2">
                  <span>
                    {c.position}
                    {c.organization && <span className="text-slate-500 dark:text-slate-400"> · {c.organization}</span>}
                    {c.unit === 'stake' && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        Stake
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {c.started_on}{c.ended_on ? ` → ${c.ended_on}` : ' → present'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <PersonTagsCard person={p} />
      </section>

      <section className="space-y-4">
        <NotesCard person={p} />
        {hh && <HouseholdNotesCard household={hh} />}
        <InteractionsSection
          personId={p.id}
          householdId={p.household_id}
          isSingleOccupant={isSingleOccupant}
          interactions={interactions.data?.interactions ?? []}
        />
        <div className="print:hidden">
          <h2 className="mb-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Change history</h2>
          <ChangeLog resourceType="person" resourceId={p.id} limit={50} />
        </div>
      </section>
    </div>
  );
}

function InteractionsSection({
  personId,
  householdId,
  isSingleOccupant,
  interactions,
}: {
  personId: string;
  householdId: string | null;
  isSingleOccupant: boolean;
  interactions: Interaction[];
}) {
  const [logging, setLogging] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const deleteInteraction = useDeleteInteraction();

  // In a single-occupant household, default to logging on the household so
  // there's a single canonical place for the record (and it stays consistent
  // if a spouse later joins).
  const logOnHousehold = isSingleOccupant && !!householdId;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Interactions</h2>
        {!logging && (
          <button
            onClick={() => setLogging(true)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + Log interaction
          </button>
        )}
      </div>
      {logging && (
        <div className="mb-3">
          {logOnHousehold && (
            <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
              Single-occupant household — logging on the household.
            </p>
          )}
          <LogInteractionForm
            personIds={logOnHousehold ? [] : [personId]}
            householdIds={logOnHousehold ? [householdId!] : []}
            onDone={() => setLogging(false)}
            onCancel={() => setLogging(false)}
          />
        </div>
      )}
      {interactions.length === 0 && !logging ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No interactions logged for this person yet.</p>
      ) : interactions.length > 0 ? (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          {interactions.map((i) => (
            <li key={i.id} className="px-3 py-2 text-sm">
              {editingId === i.id ? (
                <InteractionEditForm
                  interaction={i}
                  onDone={() => setEditingId(null)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
              <>
              <div className="flex justify-between">
                <span className="flex items-center gap-2 font-medium">
                  {formatInteractionType(i.type)}
                  {i.performed_by && (
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400">by {i.performed_by}</span>
                  )}
                  {i.linked_via === 'household' && (
                    <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      household
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(i.occurred_at).toLocaleString()}
                  </span>
                  <button
                    onClick={() => setEditingId(i.id)}
                    className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => { if (confirm('Delete this interaction?')) deleteInteraction.mutate(i.id); }}
                    className="text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {i.notes && (
                <div className="prose dark:prose-invert prose-sm mt-1 max-w-none text-slate-700 dark:text-slate-300">
                  <ReactMarkdown>{i.notes}</ReactMarkdown>
                </div>
              )}
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{i.created_by}</p>
              </>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function HouseholdNotesCard({ household: h }: { household: Household }) {
  const patch = usePatchHousehold();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const start = () => { setDraft(h.notes_override ?? ''); setEditing(true); };
  const cancel = () => { setEditing(false); patch.reset(); };
  const save = () => {
    const next = draft.trim() ? draft : null;
    if (next === (h.notes_override ?? null)) { cancel(); return; }
    patch.mutate({ id: h.id, body: { notes_override: next } }, { onSuccess: () => setEditing(false) });
  };

  if (!editing && !h.notes_override) {
    return (
      <div className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
        <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={start}>
          + Add household notes
        </button>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Household notes</span>
        {editing ? (
          <div className="flex gap-1">
            <button className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50" onClick={save} disabled={patch.isPending}>
              {patch.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400" onClick={cancel} disabled={patch.isPending}>
              Cancel
            </button>
          </div>
        ) : (
          <button className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={start}>Edit</button>
        )}
      </div>
      <div className="px-3 py-2">
        {editing ? (
          <textarea
            className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Notes about the household (context that doesn't belong on a single person)."
          />
        ) : (
          <div className="prose dark:prose-invert prose-sm max-w-none">
            <ReactMarkdown>{h.notes_override ?? ''}</ReactMarkdown>
          </div>
        )}
      </div>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function PersonTagsCard({ person: p }: { person: Person }) {
  const patch = usePatchPerson();
  return (
    <TagsCard
      tags={p.tags ?? []}
      saving={patch.isPending}
      error={patch.error as Error | null}
      onSave={(next) => patch.mutate({ id: p.id, body: { tags: next } })}
    />
  );
}

function showPriesthood(p: Person): boolean {
  if (p.sex === 'F') return false;
  if (p.age != null && p.age <= 10) return false;
  if (p.is_member === false) return false;
  return true;
}

function PersonInfoCard({ person: p }: { person: Person }) {
  const patch = usePatchPerson();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PersonEdits>({});

  const start = () => {
    setDraft({
      name_full: p.name_full,
      phone: p.phone,
      email: p.email,
      birth_date: p.birth_date,
      household_role: p.household_role,
      status_override: p.status_override,
      occupation: p.occupation,
    });
    setEditing(true);
  };
  const cancel = () => { setDraft({}); setEditing(false); patch.reset(); };
  const save = () => {
    const body = diffFields(draft, p);
    if (Object.keys(body).length === 0) { cancel(); return; }
    patch.mutate({ id: p.id, body }, { onSuccess: () => { setEditing(false); setDraft({}); } });
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader title="Profile" editing={editing} onEdit={start} onSave={save} onCancel={cancel} saving={patch.isPending} />
      <dl>
        <Row label="Preferred name">
          {editing ? (
            <Input value={draft.name_full ?? ''} onChange={(v) => setDraft({ ...draft, name_full: v || null })} placeholder="Full preferred name" />
          ) : display(p.name_full)}
        </Row>
        <Row label="Phone">
          {editing ? (
            <Input type="tel" value={draft.phone ?? ''} onChange={(v) => setDraft({ ...draft, phone: v || null })} placeholder="555-555-5555" />
          ) : phoneLink(p.phone)}
        </Row>
        <Row label="Email">
          {editing ? (
            <Input type="email" value={draft.email ?? ''} onChange={(v) => setDraft({ ...draft, email: v || null })} placeholder="name@example.com" />
          ) : emailLink(p.email)}
        </Row>
        <Row label="Birthdate">
          {editing ? (
            <Input type="date" value={draft.birth_date ?? ''} onChange={(v) => setDraft({ ...draft, birth_date: v || null })} />
          ) : display(p.birth_date)}
        </Row>
        <Row label="Occupation">
          {editing ? (
            <Input value={draft.occupation ?? ''} onChange={(v) => setDraft({ ...draft, occupation: v || null })} placeholder="Job, employer, or what they do" />
          ) : display(p.occupation)}
        </Row>
        <Row label="Household role">
          {editing ? (
            <select
              className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              value={draft.household_role ?? ''}
              onChange={(e) => setDraft({ ...draft, household_role: (e.target.value || null) as HouseholdRole | null })}
            >
              <option value="">—</option>
              <option value="head">Head</option>
              <option value="spouse">Spouse</option>
              <option value="other">Other</option>
            </select>
          ) : display(formatEnum(p.household_role))}
        </Row>
        <Row label="Status">
          {editing ? (
            <select
              className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              value={draft.status_override ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, status_override: (e.target.value || null) as PersonStatus | null })
              }
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : display(formatEnum(p.status_override))}
        </Row>
        {showPriesthood(p) && (
          <Row label="Priesthood">{display(formatEnum(p.priesthood_office))}</Row>
        )}
      </dl>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function NotesCard({ person: p }: { person: Person }) {
  const patch = usePatchPerson();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => { setDraft(p.notes_override ?? ''); setEditing(true); };
  const cancel = () => { setEditing(false); patch.reset(); };
  const save = () => {
    const next = draft.trim() ? draft : null;
    if (next === (p.notes_override ?? null)) { cancel(); return; }
    patch.mutate({ id: p.id, body: { notes_override: next } }, { onSuccess: () => setEditing(false) });
  };

  if (!editing && !p.notes_override) {
    return (
      <div className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
        <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={start}>+ Add notes</button>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <CardHeader title="Notes" editing={editing} onEdit={start} onSave={save} onCancel={cancel} saving={patch.isPending} />
      <div className="px-3 py-2">
        {editing ? (
          <textarea className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Anything worth remembering about this person." />
        ) : (
          <div className="prose dark:prose-invert prose-sm max-w-none">
            <ReactMarkdown>{p.notes_override ?? ''}</ReactMarkdown>
          </div>
        )}
      </div>
      {patch.error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function CardHeader({ title, editing, saving, onEdit, onSave, onCancel }: {
  title: string; editing: boolean; saving: boolean;
  onEdit: () => void; onSave: () => void; onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</span>
      {editing ? (
        <div className="flex gap-1">
          <button className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400" onClick={onCancel} disabled={saving}>Cancel</button>
        </div>
      ) : (
        <button className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300" onClick={onEdit}>Edit</button>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 border-b border-slate-100 dark:border-slate-800 px-3 py-1 text-sm last:border-b-0">
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="col-span-2 text-slate-700 dark:text-slate-300">{children}</dd>
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder }: {
  value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <input className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function display(v: string | null | undefined): React.ReactNode {
  return v ? v : <span className="text-slate-400 dark:text-slate-500">—</span>;
}

function phoneLink(v: string | null | undefined): React.ReactNode {
  if (!v) return display(v);
  return <a href={telHref(v)} className="hover:underline">{formatPhone(v)}</a>;
}

function emailLink(v: string | null | undefined): React.ReactNode {
  if (!v) return display(v);
  return <a href={`mailto:${v}`} className="hover:underline">{v}</a>;
}

function diffFields<T extends Record<string, unknown>>(draft: T, current: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(draft) as (keyof T)[]) {
    if (draft[k] !== current[k]) out[k] = draft[k];
  }
  return out;
}
